import React, { useState } from 'react';
import { Save, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useAdminMarketingSettings } from '../../hooks/useMarketingSettings';
import type { PlatformMarketing } from '../../hooks/useMarketingSettings';

const VALIDATORS: Record<string, { pattern: RegExp; message: string }> = {
  gtm: { pattern: /^GTM-[A-Z0-9]+$/, message: 'Formato: GTM-XXXXXXX' },
  ga4: { pattern: /^G-[A-Z0-9]+$/, message: 'Formato: G-XXXXXXXXXX' },
  metaPixel: { pattern: /^\d+$/, message: 'Apenas números' },
};

interface ToggleProps {
  enabled: boolean;
  onChange: (v: boolean) => void;
}

const Toggle: React.FC<ToggleProps> = ({ enabled, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!enabled)}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
      enabled ? 'bg-green-500' : 'bg-neutral-700'
    }`}
  >
    <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
  </button>
);

export const AdminMarketing: React.FC = () => {
  const { config, loading, saving, save, setConfig } = useAdminMarketingSettings();
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [saveResult, setSaveResult] = useState<'success' | 'error' | null>(null);

  const validate = (cfg: PlatformMarketing): boolean => {
    const errors: Record<string, string> = {};

    if (cfg.gtm.enabled && cfg.gtm.containerId && !VALIDATORS.gtm.pattern.test(cfg.gtm.containerId)) {
      errors.gtm = VALIDATORS.gtm.message;
    }
    if (cfg.ga4.enabled && cfg.ga4.measurementId && !VALIDATORS.ga4.pattern.test(cfg.ga4.measurementId)) {
      errors.ga4 = VALIDATORS.ga4.message;
    }
    if (cfg.metaPixel.enabled && cfg.metaPixel.pixelId && !VALIDATORS.metaPixel.pattern.test(cfg.metaPixel.pixelId)) {
      errors.metaPixel = VALIDATORS.metaPixel.message;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validate(config)) return;
    const { error } = await save(config);
    setSaveResult(error ? 'error' : 'success');
    setTimeout(() => setSaveResult(null), 3000);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6">Pixel & Marketing</h1>

      <div className="space-y-6 max-w-2xl">
        {/* GTM */}
        <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-medium text-white">Google Tag Manager</h3>
              <p className="text-xs text-neutral-500 mt-0.5">Gerenciamento de tags centralizado</p>
            </div>
            <Toggle
              enabled={config.gtm.enabled}
              onChange={v => setConfig({ ...config, gtm: { ...config.gtm, enabled: v } })}
            />
          </div>
          {config.gtm.enabled && (
            <div>
              <label className="text-xs text-neutral-400">Container ID</label>
              <input
                type="text"
                value={config.gtm.containerId}
                onChange={e => setConfig({ ...config, gtm: { ...config.gtm, containerId: e.target.value.toUpperCase() } })}
                placeholder="GTM-XXXXXXX"
                className="w-full mt-1 bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-base sm:text-sm text-white placeholder-neutral-600"
              />
              {validationErrors.gtm && <p className="text-xs text-red-400 mt-1">{validationErrors.gtm}</p>}
            </div>
          )}
        </div>

        {/* GA4 */}
        <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-medium text-white">Google Analytics 4</h3>
              <p className="text-xs text-neutral-500 mt-0.5">Análise de comportamento e conversões</p>
            </div>
            <Toggle
              enabled={config.ga4.enabled}
              onChange={v => setConfig({ ...config, ga4: { ...config.ga4, enabled: v } })}
            />
          </div>
          {config.ga4.enabled && (
            <div>
              <label className="text-xs text-neutral-400">Measurement ID</label>
              <input
                type="text"
                value={config.ga4.measurementId}
                onChange={e => setConfig({ ...config, ga4: { ...config.ga4, measurementId: e.target.value.toUpperCase() } })}
                placeholder="G-XXXXXXXXXX"
                className="w-full mt-1 bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-base sm:text-sm text-white placeholder-neutral-600"
              />
              {validationErrors.ga4 && <p className="text-xs text-red-400 mt-1">{validationErrors.ga4}</p>}
            </div>
          )}
        </div>

        {/* Meta Pixel */}
        <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-medium text-white">Meta Pixel (Facebook)</h3>
              <p className="text-xs text-neutral-500 mt-0.5">Tracking de conversões e remarketing</p>
            </div>
            <Toggle
              enabled={config.metaPixel.enabled}
              onChange={v => setConfig({ ...config, metaPixel: { ...config.metaPixel, enabled: v } })}
            />
          </div>
          {config.metaPixel.enabled && (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-neutral-400">Pixel ID</label>
                <input
                  type="text"
                  value={config.metaPixel.pixelId}
                  onChange={e => setConfig({ ...config, metaPixel: { ...config.metaPixel, pixelId: e.target.value } })}
                  placeholder="123456789012345"
                  className="w-full mt-1 bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-base sm:text-sm text-white placeholder-neutral-600"
                />
                {validationErrors.metaPixel && <p className="text-xs text-red-400 mt-1">{validationErrors.metaPixel}</p>}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={config.metaPixel.advancedMatching}
                  onChange={e => setConfig({ ...config, metaPixel: { ...config.metaPixel, advancedMatching: e.target.checked } })}
                  className="rounded border-white/10"
                />
                <label className="text-xs text-neutral-400">Advanced Matching</label>
              </div>
            </div>
          )}
        </div>

        {/* Meta CAPI */}
        <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-medium text-white">Meta Conversions API (CAPI)</h3>
              <p className="text-xs text-neutral-500 mt-0.5">Server-side tracking para maior precisão</p>
            </div>
            <Toggle
              enabled={config.metaCAPI.enabled}
              onChange={v => setConfig({ ...config, metaCAPI: { ...config.metaCAPI, enabled: v } })}
            />
          </div>
          {config.metaCAPI.enabled && (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-neutral-400">Access Token</label>
                <input
                  type="password"
                  value={config.metaCAPI.accessToken}
                  onChange={e => setConfig({ ...config, metaCAPI: { ...config.metaCAPI, accessToken: e.target.value } })}
                  placeholder="EAAxxxxxxx..."
                  className="w-full mt-1 bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-base sm:text-sm text-white placeholder-neutral-600"
                />
              </div>
              <div>
                <label className="text-xs text-neutral-400">Test Event Code (opcional)</label>
                <input
                  type="text"
                  value={config.metaCAPI.testEventCode || ''}
                  onChange={e => setConfig({ ...config, metaCAPI: { ...config.metaCAPI, testEventCode: e.target.value } })}
                  placeholder="TEST12345"
                  className="w-full mt-1 bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-base sm:text-sm text-white placeholder-neutral-600"
                />
              </div>
            </div>
          )}
        </div>

        {/* Save Button */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/15 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Salvando...' : 'Salvar Configurações'}
          </button>
          {saveResult === 'success' && (
            <span className="flex items-center gap-1 text-sm text-green-400">
              <CheckCircle className="w-4 h-4" /> Salvo com sucesso
            </span>
          )}
          {saveResult === 'error' && (
            <span className="flex items-center gap-1 text-sm text-red-400">
              <AlertCircle className="w-4 h-4" /> Erro ao salvar
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
